import { IncentivesApiError, parseResponse } from './types'
import { validShareRef } from './invitationLinks'
interface Proof { ref: string; token: string; expires: number; owner: string | null; requestId: string }
export interface VisitState { phase: 'idle' | 'opening' | 'ready' | 'binding' | 'bound' | 'kept_other' | 'error'; error: string | null; retryAt: number }
const key = 'musicmint:invitation-proof:v1'
/** One tab, one bounded proof; it is never added to a URL, analytics, metadata or a card. */
export function createInvitationVisit(state: VisitState, dependencies: {
  enabled: () => boolean; uuid: () => string; token: () => Promise<string | null | undefined>
  storage: () => Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>; fetch: typeof fetch
  subject: () => string | null; invalidate: () => void; now?: () => number
}) {
  const now = dependencies.now || Date.now, attempts = new Map<string, { event_id: string; anonymous_id: string }>()
  let owner = '', initialized = false, proof: Proof | null = null, generation = 0, request: AbortController | null = null
  let attemptedBinding = ''
  const erase = () => { proof = null; attemptedBinding = ''; try { dependencies.storage().removeItem(key) } catch { /* In-memory isolation still applies. */ } }
  const save = () => {
    try {
      const storage = dependencies.storage(), serialized = proof ? JSON.stringify(proof) : null
      if (serialized) storage.setItem(key, serialized); else storage.removeItem(key)
      if (storage.getItem(key) !== serialized) throw new Error('storage_unavailable')
      return true
    } catch {
      // Memory-only is safe only if a prior anonymous proof cannot survive under an unclaimed owner.
      try { const storage = dependencies.storage(); storage.removeItem(key); return storage.getItem(key) === null } catch { return false }
    }
  }
  function restore() {
    try {
      const raw = dependencies.storage().getItem(key)
      if (!raw) return
      const candidate = JSON.parse(raw)
      if (!validShareRef(candidate.ref) || typeof candidate.token !== 'string' || candidate.token.length > 8192 || !candidate.token || !Number.isSafeInteger(candidate.expires) || candidate.expires <= now() || !/^[0-9a-f-]{36}$/i.test(candidate.requestId) || candidate.owner !== null && candidate.owner !== owner) { erase(); return }
      proof = candidate
    } catch { erase() }
  }
  function failed(error: unknown, message: string) {
    state.phase = 'error'; state.error = message
    if (error instanceof IncentivesApiError && error.status === 429) {
      const delay = error.retryAfter && /^\d+$/.test(error.retryAfter) ? now() + Number(error.retryAfter) * 1000 : Date.parse(error.retryAfter || '')
      state.retryAt = Number.isFinite(delay) ? Math.max(now(), delay) : now() + 60000
    }
  }
  async function bind(retry = false) {
    if (now() < state.retryAt || !dependencies.enabled() || !owner || !proof || state.phase === 'binding') return
    if (proof.expires <= now() || proof.owner !== null && proof.owner !== owner) { erase(); state.phase = 'idle'; return }
    proof.owner = owner
    if (!save()) { state.phase = 'error'; state.error = 'attributionFailed'; return }
    const value = proof, context = owner, version = generation, subject = dependencies.subject()
    if (!retry && attemptedBinding === value.requestId) return
    attemptedBinding = value.requestId
    request?.abort(); const controller = new AbortController(); request = controller
    const current = () => version === generation && context === owner && proof === value && !controller.signal.aborted
    state.phase = 'binding'; state.error = null
    try {
      const token = await dependencies.token()
      if (!current()) return
      if (!token) throw new Error('unauthorized')
      const response = await dependencies.fetch('/__invitations/attribution', { method: 'POST', credentials: 'same-origin', redirect: 'error', signal: controller.signal, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ schema_version: 2, request_id: value.requestId, invite_ref: value.ref, invite_open_token: value.token }) })
      const result = parseResponse('invitations/attribution', response.status, await response.json(), response.headers.get('retry-after'))
      if (!current()) return
      if (subject && result.subject_id !== subject) { erase(); dependencies.invalidate(); state.phase = 'error'; state.error = 'attributionFailed'; return }
      erase(); state.phase = result.relation_result === 'kept_other' ? 'kept_other' : 'bound'
    } catch (error) { if (current()) failed(error, 'attributionFailed') }
  }
  function setIdentity(value: string, ready: boolean) {
    if (!ready || initialized && value === owner) return
    const previous = owner, interrupted = state.phase === 'opening'; owner = value; generation++; request?.abort(); request = null
    if (!initialized) { initialized = true; restore() }
    else if (previous && previous !== value) { erase(); attempts.clear() }
    if (proof && proof.owner !== null && proof.owner !== value) erase()
    state.phase = proof ? 'ready' : interrupted ? 'error' : 'idle'; state.error = interrupted ? 'openFailed' : null; state.retryAt = 0
    if (owner && proof) void bind()
  }
  async function open(ref: string, retry = false) {
    if (now() < state.retryAt || !initialized || !dependencies.enabled() || !validShareRef(ref) || state.phase === 'opening' || attempts.has(ref) && !retry) return
    if (proof && proof.expires > now()) { if (owner) void bind(retry); return }
    if (!attempts.has(ref)) attempts.set(ref, { event_id: dependencies.uuid(), anonymous_id: dependencies.uuid() })
    const context = owner, version = generation, controller = new AbortController(); request?.abort(); request = controller
    const current = () => version === generation && context === owner && !controller.signal.aborted
    state.phase = 'opening'; state.error = null
    try {
      const token = owner ? await dependencies.token() : null
      if (!current()) return
      const response = await dependencies.fetch('/__invitations/open', { method: 'POST', credentials: 'same-origin', redirect: 'error', signal: controller.signal, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify({ share_ref: ref, ...attempts.get(ref) }) })
      const result = parseResponse('public/invitations/open', response.status, await response.json(), response.headers.get('retry-after'))
      if (!current()) return
      if (!result.accepted || !result.invite_open_token || !result.expires_at || result.expires_at <= now()) { state.phase = 'idle'; return }
      proof = { ref, token: result.invite_open_token, expires: result.expires_at, owner: owner || null, requestId: dependencies.uuid() }; save(); state.phase = 'ready'
      if (owner) void bind()
    } catch (error) { if (current()) failed(error, 'openFailed') }
  }
  return { state, setIdentity, open, retry: () => bind(true), dispose() { generation++; request?.abort(); request = null } }
}
